import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Download, Upload, HardDrive, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminBackup() {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restoreInputRef = useRef(null);

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/backup`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']
        ?.split('filename=')?.[1]
        || `backup_${new Date().toISOString().slice(0, 10)}.zip`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  const handleRestoreBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      toast.error('Please select a .zip backup file');
      return;
    }

    if (!window.confirm('Restore will overwrite your current data. Are you sure?')) {
      e.target.value = '';
      return;
    }

    setRestoring(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/restore`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Backup restored successfully. Reloading...');
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to restore backup');
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };

  return (
    <div className="p-8 max-w-2xl" data-testid="admin-backup">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Backup & Restore</h1>
        <p className="text-muted-foreground">Download or restore your site data</p>
      </div>

      <div className="border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <HardDrive className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Site Data</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          Download a full backup of your posts, pages, settings, and uploaded images as a ZIP file.
          Restore from a previous backup to recover your data.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            type="button"
            onClick={handleDownloadBackup}
            disabled={downloading}
            data-testid="download-backup-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            {downloading ? 'Downloading...' : 'Download Backup'}
          </Button>

          <div>
            <input
              type="file"
              ref={restoreInputRef}
              onChange={handleRestoreBackup}
              accept=".zip"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => restoreInputRef.current?.click()}
              disabled={restoring}
              data-testid="restore-backup-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              {restoring ? 'Restoring...' : 'Restore Backup'}
            </Button>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>Restoring a backup will overwrite all current data. Make sure to download a backup first.</span>
        </div>
      </div>
    </div>
  );
}
